import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../lib/api'
import { fmtDate, type PageMeta } from '../lib/types'
import { StatusBadge } from '../components/StatusBadge'

/* —— Categories —— */
type Cat = {
  id: string
  slug: string
  name: string
  name_ru: string
  is_active: boolean
  sort_order: number
  parent_id?: string | null
}

export function CategoriesPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => api<{ items: Cat[] }>('/admin/categories?include_inactive=true'),
  })
  const [form, setForm] = useState({ slug: '', name: '', name_ru: '', sort_order: 0 })
  const create = useMutation({
    mutationFn: () => api('/admin/categories', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-categories'] })
      setForm({ slug: '', name: '', name_ru: '', sort_order: 0 })
    },
  })
  const toggle = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api(`/admin/categories/${id}`, { method: 'PATCH', body: JSON.stringify({ is_active }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-categories'] }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/categories/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-categories'] }),
  })

  return (
    <div>
      <h1 className="page-title">Категории</h1>
      <p className="page-sub">CRUD таксономии услуг</p>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <input className="input" placeholder="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          <input className="input" placeholder="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="input" placeholder="name_ru" value={form.name_ru} onChange={(e) => setForm({ ...form, name_ru: e.target.value })} />
          <input className="input" type="number" style={{ width: 80 }} value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          <button className="btn" disabled={!form.slug || !form.name_ru || create.isPending} onClick={() => create.mutate()}>Добавить</button>
        </div>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Порядок</th>
              <th>RU</th>
              <th>Slug</th>
              <th>Активна</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5}>Загрузка…</td></tr>}
            {data?.items.map((c) => (
              <tr key={c.id}>
                <td>{c.sort_order}</td>
                <td>{c.name_ru}{c.parent_id ? ' ↳' : ''}</td>
                <td>{c.slug}</td>
                <td><StatusBadge value={c.is_active ? 'active' : 'inactive'} /></td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="btn" onClick={() => toggle.mutate({ id: c.id, is_active: !c.is_active })}>
                    {c.is_active ? 'Выкл' : 'Вкл'}
                  </button>
                  {c.is_active && (
                    <button className="btn btn-danger" onClick={() => remove.mutate(c.id)}>Скрыть</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* —— Analytics —— */
type Funnel = {
  days: number
  steps: { key: string; label: string; count: number; conversion_from_prev?: number | null }[]
  series: { date: string; registrations: number; orders: number; proposals: number; deals: number; payments: number }[]
}

export function AnalyticsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-funnel'],
    queryFn: () => api<Funnel>('/admin/analytics/funnel?days=30'),
  })
  if (isLoading) return <p className="muted">Загрузка…</p>
  if (error || !data) return <div className="error">{(error as Error)?.message || 'Ошибка'}</div>

  return (
    <div>
      <h1 className="page-title">Аналитика</h1>
      <p className="page-sub">Воронка за {data.days} дней</p>
      <div className="grid-kpi">
        {data.steps.map((s) => (
          <div className="card" key={s.key}>
            <div className="kpi-label">{s.label}</div>
            <div className="kpi-value">{s.count}</div>
            {s.conversion_from_prev != null && (
              <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>{s.conversion_from_prev}% от пред.</div>
            )}
          </div>
        ))}
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Воронка</h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.steps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#0037b0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Динамика</h3>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="registrations" stackId="1" stroke="#0037b0" fill="#bfdbfe" name="Рег." />
              <Area type="monotone" dataKey="orders" stackId="1" stroke="#0b7a52" fill="#bbf7d0" name="Заказы" />
              <Area type="monotone" dataKey="deals" stackId="1" stroke="#b45309" fill="#fde68a" name="Сделки" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

/* —— Chats —— */
type ChatRow = {
  id: string
  title?: string | null
  deal_number?: string | null
  last_message_preview?: string | null
  last_message_at?: string | null
  member_count: number
}

type Msg = {
  id: string
  sender_name?: string | null
  body?: string | null
  is_system: boolean
  created_at?: string | null
}

export function ChatsPage() {
  const [selected, setSelected] = useState<string | null>(null)
  const { data, isLoading } = useQuery({
    queryKey: ['admin-chats'],
    queryFn: () => api<{ items: ChatRow[]; meta: PageMeta }>('/admin/chats?page_size=50'),
  })
  const msgs = useQuery({
    queryKey: ['admin-chat-msgs', selected],
    queryFn: () => api<{ items: Msg[] }>(`/admin/chats/${selected}/messages?page_size=200`),
    enabled: !!selected,
  })

  return (
    <div>
      <h1 className="page-title">Чаты</h1>
      <p className="page-sub">Только чтение · каждый просмотр пишется в Audit Log</p>
      <div className="two-col">
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Сделка</th>
                <th>Превью</th>
                <th>Участн.</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={3}>Загрузка…</td></tr>}
              {data?.items.map((c) => (
                <tr
                  key={c.id}
                  style={{ cursor: 'pointer', background: selected === c.id ? '#eff6ff' : undefined }}
                  onClick={() => setSelected(c.id)}
                >
                  <td>{c.deal_number || c.title || c.id.slice(0, 8)}</td>
                  <td style={{ whiteSpace: 'normal' }}>{c.last_message_preview || '—'}</td>
                  <td>{c.member_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Сообщения</h3>
          {!selected && <p className="muted">Выберите чат слева</p>}
          {msgs.isLoading && <p className="muted">Загрузка…</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 480, overflow: 'auto' }}>
            {msgs.data?.items.map((m) => (
              <div key={m.id} style={{ padding: 8, background: m.is_system ? '#f8fafc' : '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <div className="muted" style={{ fontSize: 11 }}>
                  {m.is_system ? 'Система' : (m.sender_name || '—')} · {fmtDate(m.created_at)}
                </div>
                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{m.body || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* —— Promo + Push —— */
type PromoOrder = {
  id: string
  title: string
  author_name?: string | null
  bump_expires_at?: string | null
  premium_expires_at?: string | null
  status: string
}

export function PromoPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['promo-orders'],
    queryFn: () => api<{ items: PromoOrder[] }>('/admin/promo/orders'),
  })
  const expire = useMutation({
    mutationFn: (id: string) => api(`/admin/promo/orders/${id}/expire`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promo-orders'] }),
  })
  const [push, setPush] = useState({ title: '', body: '' })
  const broadcast = useMutation({
    mutationFn: (dry_run: boolean) =>
      api<{ queued: number }>('/admin/push/broadcast', {
        method: 'POST',
        body: JSON.stringify({ ...push, dry_run }),
      }),
  })

  return (
    <div>
      <h1 className="page-title">Промо и пуши</h1>
      <p className="page-sub">Поднятие / премиум заказов и рассылка уведомлений</p>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Broadcast</h3>
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <input className="input" placeholder="Заголовок" value={push.title} onChange={(e) => setPush({ ...push, title: e.target.value })} />
          <input className="input" style={{ minWidth: 280 }} placeholder="Текст" value={push.body} onChange={(e) => setPush({ ...push, body: e.target.value })} />
          <button className="btn" disabled={!push.title || !push.body || broadcast.isPending} onClick={() => broadcast.mutate(false)}>
            Отправить
          </button>
          <button className="btn" disabled={!push.title || !push.body} onClick={() => broadcast.mutate(true)}>
            Dry-run
          </button>
        </div>
        {broadcast.data && <p className="muted" style={{ marginTop: 8 }}>В очереди: {broadcast.data.queued}</p>}
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Заказ</th>
              <th>Автор</th>
              <th>Bump до</th>
              <th>Premium до</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5}>Загрузка…</td></tr>}
            {data?.items.map((o) => (
              <tr key={o.id}>
                <td style={{ whiteSpace: 'normal' }}>{o.title}</td>
                <td>{o.author_name || '—'}</td>
                <td>{fmtDate(o.bump_expires_at)}</td>
                <td>{fmtDate(o.premium_expires_at)}</td>
                <td>
                  <button className="btn btn-danger" onClick={() => expire.mutate(o.id)}>Снять</button>
                </td>
              </tr>
            ))}
            {!isLoading && data?.items.length === 0 && (
              <tr><td colSpan={5} className="muted">Активных промо нет</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
